import dayjs from "@tether-web-portal/dayjs-setup";
import { deviceOnlineState } from "../utils/deviceOnline";
import { DeviceType } from "./DeviceType";
import { GraphColor } from "./Room";
import { ActiveTenantSummary } from "./PropertyTenant";

/**
 * A physical circuit on a property.
 * A property can have more than one circuit associated.
 */
export interface Circuit {
  id: string;
  propertyId: string;
  circuitNodeId: string;
  installationLocationId?: string;
  name: string;
  color: GraphColor;
  type: "TOTAL" | "PHASE";
  phase: 1 | 2 | 3 | null;
  /** Additive field on circuit-node-detail responses (Phase 1c). */
  activeTenant?: ActiveTenantSummary | null;
}

export interface CircuitDeviceStatus {
  hotdropId?: string;
  oldFirmware?: boolean;
  network?: "SPARK" | "GATEWAY";
  rssi?: number;
  snr?: number;
  averageMessagePeriod?: number;
  gatewayEui?: string;
}

export interface CircuitDeviceStatusWithReading extends CircuitDeviceStatus {
  battery?: number;
  signal?: number;
  lastMessageReceivedAt?: string;
  lastSeenAt?: string;
}

export interface CircuitWithReading extends Circuit {
  image?: string;
  device?: {
    id: string;
    type: DeviceType;
    hardwareVersion: string;
    shortSerial?: string;
    serial?: string;
    sku?: string;
    createdAt: string;
    status: CircuitDeviceStatusWithReading;
    subDeviceIndex: number;
  };
}

export interface CircuitWithoutReading extends Circuit {
  image?: string;
  device?: {
    id: string;
    type: DeviceType;
    hardwareVersion: string;
    shortSerial?: string;
    sku?: string;
    createdAt: string;
    status: CircuitDeviceStatus;
    subDeviceIndex: number;
  };
}

export enum CircuitDeviceConnectionStatus {
  NEVER_ONLINE = "Never Seen",
  OFFLINE = "Offline",
  CHARGING = "Charging...",
  ONLINE = "Online",
}

/**
 * How recently the network must have seen a circuit device for it to count as CHARGING.
 *
 * Fixed, and deliberately NOT the device type's catalog tolerance. That tolerance describes how often
 * a device delivers SAMPLES; a network sighting is a different quantity — a HotDrop holds a radio link
 * while it charges up and delivers nothing. Tying this window to the sample tolerance would silently
 * widen or erase CHARGING whenever a type's number changed.
 */
export const CIRCUIT_NETWORK_SIGHTING_WINDOW_MINUTES = 120;

/**
 * `deviceType` is required (pass `undefined` explicitly when a caller has none, which applies the
 * default tolerance) so that a new caller cannot silently judge every circuit device by two hours.
 */
export const getCircuitDeviceConnectionStatus = (
  hotdropStatus: CircuitDeviceStatusWithReading,
  deviceType: DeviceType | string | undefined,
): CircuitDeviceConnectionStatus => {
  const { lastMessageReceivedAt, lastSeenAt } = hotdropStatus;
  if (!lastMessageReceivedAt && !lastSeenAt) {
    return CircuitDeviceConnectionStatus.NEVER_ONLINE;
  }

  // The last message follows the device's uplink cadence, so it is judged by the type's tolerance. A
  // missing timestamp is `unknown`, never "now": a device seen on the network that has never delivered
  // a message must not read ONLINE.
  if (deviceOnlineState(lastMessageReceivedAt, deviceType) === "online") {
    return CircuitDeviceConnectionStatus.ONLINE;
  }

  // CHARGING has no threshold of its own: it is the gap between the two windows — seen on the network
  // within the fixed sighting window, but no message within the type's tolerance.
  if (seenOnNetworkRecently(lastSeenAt)) {
    return CircuitDeviceConnectionStatus.CHARGING;
  }

  return CircuitDeviceConnectionStatus.OFFLINE;
};

/** Same boundary rule as `deviceOnlineState`: milliseconds, and exactly on the window still counts. */
const seenOnNetworkRecently = (lastSeenAt: string | undefined): boolean => {
  if (!lastSeenAt) return false;
  const seen = dayjs(lastSeenAt);
  return seen.isValid() && dayjs().diff(seen) <= CIRCUIT_NETWORK_SIGHTING_WINDOW_MINUTES * 60_000;
};
